/**
 * TankModal — Task 25.6 (DEC-117). Add or edit a standard tank catalog type.
 * Mirrors the dispatcher AddTankModal field idiom (name / capacity / bbl-per-inch).
 *
 * DEC-379 (0265): no tank type. The row has a shape (Round / Rectangular), the dimensions
 * that match it (diameter, or length and width; height for both) and a gross capacity, all
 * optional. When the dimensions are there, a typed BBL per inch more than 5% from the
 * dimension-based estimate shows a warning as you type (shared bblPerInchWarning, also used
 * by the table). It never blocks saving: the typed value comes from the strapping chart.
 */
import * as React from 'react';
import { toast } from 'sonner';
import { bblPerInchWarning, type TankShape } from '@lodex-pro/shared/tank-strapping';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { FormRow, fieldStyle, labelStyle } from '../../ui/bits';
import { useCreateTank, useUpdateTank, type StandardTank, type TankDimensionsBody } from './hooks';
import { formatNumber } from '@lodex-pro/shared/number-format';

export const TANK_SHAPES: readonly { value: TankShape; label: string }[] = [
  { value: 'ROUND', label: 'Round' },
  { value: 'RECTANGULAR', label: 'Rectangular' },
];

export interface TankModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** null → add; a row → edit. */
  readonly editing: StandardTank | null;
}

function toNum(s: string): number | undefined {
  if (s.trim() === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

/** An optional positive field: '' is none (null); anything else must be a positive number. */
function optionalPositive(s: string): number | null | 'bad' {
  if (s.trim() === '') return null;
  const n = toNum(s);
  return n === undefined || n <= 0 ? 'bad' : n;
}

function NumberInput({
  id,
  value,
  onChange,
  step,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  step: string;
}): React.JSX.Element {
  return (
    <input
      id={id}
      type="number"
      min={0}
      step={step}
      value={value}
      onChange={(e): void => onChange(e.target.value)}
      style={fieldStyle}
    />
  );
}

export function TankModal({ open, onOpenChange, editing }: TankModalProps): React.JSX.Element {
  const isEdit = editing !== null;
  const [name, setName] = React.useState('');
  const [capacity, setCapacity] = React.useState('');
  const [grossCapacity, setGrossCapacity] = React.useState('');
  const [bblPerInch, setBblPerInch] = React.useState('');
  const [shape, setShape] = React.useState<TankShape>('ROUND');
  const [diameter, setDiameter] = React.useState('');
  const [length, setLength] = React.useState('');
  const [width, setWidth] = React.useState('');
  const [height, setHeight] = React.useState('');
  const [active, setActive] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? '');
    setCapacity(editing?.capacityBbl ?? '');
    setGrossCapacity(editing?.grossCapacityBbl ?? '');
    setBblPerInch(editing?.bblPerInch ?? '');
    setShape(editing?.shape ?? 'ROUND');
    setDiameter(editing?.diameterFt ?? '');
    setLength(editing?.lengthFt ?? '');
    setWidth(editing?.widthFt ?? '');
    setHeight(editing?.heightFt ?? '');
    setActive(editing?.isActive ?? true);
    setError(null);
  }, [open, editing]);

  const warning = bblPerInchWarning(bblPerInch, {
    shape,
    diameterFt: diameter,
    lengthFt: length,
    widthFt: width,
  });

  const create = useCreateTank(() => {
    toast.success('Tank type added');
    onOpenChange(false);
  });
  const update = useUpdateTank(editing?.id ?? '');
  const pending = create.isPending || update.isPending;

  const save = (): void => {
    if (name.trim() === '') return setError('Name is required.');
    const cap = toNum(capacity);
    const bpi = toNum(bblPerInch);
    if (cap === undefined || cap <= 0) return setError('Capacity must be a positive number.');
    if (bpi === undefined || bpi <= 0) return setError('BBL per inch must be a positive number.');
    const round = shape === 'ROUND';
    const fields: Record<keyof TankDimensionsBody, number | null | 'bad'> = {
      diameterFt: round ? optionalPositive(diameter) : null,
      lengthFt: round ? null : optionalPositive(length),
      widthFt: round ? null : optionalPositive(width),
      heightFt: optionalPositive(height),
      grossCapacityBbl: optionalPositive(grossCapacity),
    };
    if (Object.values(fields).includes('bad')) {
      return setError('Dimensions and gross capacity must be positive numbers, or left blank.');
    }
    const dims = fields as TankDimensionsBody;
    setError(null);

    if (isEdit) {
      update.mutate(
        {
          name: name.trim(),
          capacityBbl: cap,
          bblPerInch: bpi,
          shape,
          ...dims,
          isActive: active,
        },
        {
          onSuccess: () => {
            toast.success('Tank type updated');
            onOpenChange(false);
          },
          onError: () => setError('Could not save changes.'),
        },
      );
      return undefined;
    }
    create.mutate(
      { name: name.trim(), capacityBbl: cap, bblPerInch: bpi, shape, ...dims },
      { onError: () => setError('Could not add tank type.') },
    );
    return undefined;
  };

  const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' } as const;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit tank type' : 'Add tank type'}</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <FormRow label="Name" htmlFor="tk-name" required>
            <input
              id="tk-name"
              value={name}
              onChange={(e): void => setName(e.target.value)}
              placeholder="e.g. 400 BBL 12FT"
              style={fieldStyle}
            />
          </FormRow>
          <div style={grid2}>
            <FormRow label="Capacity (BBL)" htmlFor="tk-cap" required hint="The nominal rating.">
              <NumberInput id="tk-cap" value={capacity} onChange={setCapacity} step="0.01" />
            </FormRow>
            <FormRow
              label="Gross capacity (BBL)"
              htmlFor="tk-gross"
              hint="The strapped shell: BBL per inch × height in inches."
            >
              <NumberInput
                id="tk-gross"
                value={grossCapacity}
                onChange={setGrossCapacity}
                step="0.01"
              />
            </FormRow>
          </div>
          <FormRow
            label="BBL per inch"
            htmlFor="tk-bpi"
            required
            hint="From the tank's strapping chart (up to 4 decimals)."
          >
            <NumberInput id="tk-bpi" value={bblPerInch} onChange={setBblPerInch} step="0.0001" />
          </FormRow>
          <FormRow label="Shape" htmlFor="tk-shape">
            <select
              id="tk-shape"
              value={shape}
              onChange={(e): void => setShape(e.target.value as TankShape)}
              style={{ ...fieldStyle, maxWidth: '200px' }}
            >
              {TANK_SHAPES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </FormRow>
          {shape === 'ROUND' ? (
            <div style={grid2}>
              <FormRow label="Diameter (ft)" htmlFor="tk-dia">
                <NumberInput id="tk-dia" value={diameter} onChange={setDiameter} step="0.0001" />
              </FormRow>
              <FormRow label="Height (ft)" htmlFor="tk-height">
                <NumberInput id="tk-height" value={height} onChange={setHeight} step="0.0001" />
              </FormRow>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <FormRow label="Length (ft)" htmlFor="tk-len">
                <NumberInput id="tk-len" value={length} onChange={setLength} step="0.0001" />
              </FormRow>
              <FormRow label="Width (ft)" htmlFor="tk-wid">
                <NumberInput id="tk-wid" value={width} onChange={setWidth} step="0.0001" />
              </FormRow>
              <FormRow label="Height (ft)" htmlFor="tk-height">
                <NumberInput id="tk-height" value={height} onChange={setHeight} step="0.0001" />
              </FormRow>
            </div>
          )}
          {warning !== null && (
            <p
              role="status"
              data-testid="bpi-warning"
              style={{ ...labelStyle, textTransform: 'none', color: 'var(--color-warning)' }}
            >
              {warning.message} (expected about{' '}
              {formatNumber(warning.expectedBblPerInch, { decimals: 2 })}). You can still save.
            </p>
          )}
          {isEdit && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={active}
                onChange={(e): void => setActive(e.target.checked)}
              />
              <span style={{ ...labelStyle, marginBottom: 0, textTransform: 'none' }}>
                Active (haulers can pick this when adding tanks)
              </span>
            </label>
          )}
          {error !== null && (
            <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)' }}>
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={(): void => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={pending}
            onClick={save}
            style={{ backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }}
          >
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
